import React, { useEffect, useRef } from "react";
import { SearchMenuPortal } from "../../pages/Patients/components/SearchMenuPortal";
import { rowInputClass } from "../../pages/Patients/components/MultiRowFields";
import { SearchIcon } from "../../Icons";
import { BRAND_THEME } from "../../ui/brandTheme";
import useDebouncedCatalogSearch, {
  CATALOG_SEARCH_DEBOUNCE_MS,
  CATALOG_SEARCH_MIN_CHARS,
} from "../../hooks/useDebouncedCatalogSearch";

const inputBaseClass = `
  block w-full rounded-md text-sm py-1.5 pl-2.5 pr-10
  border bg-white text-slate-900 placeholder:text-slate-400
  transition-colors duration-200 ease-in-out outline-none
  border-indigo-200 hover:border-indigo-300
  ${BRAND_THEME.fieldFocus}
`;

const SearchMenuLoading = () => (
  <div className="px-3 py-4 flex items-center justify-center gap-2 text-sm text-slate-500">
    <span className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin shrink-0" />
    <span>Searching…</span>
  </div>
);

/**
 * Reusable typeahead for Libraries catalog names (pharmacy, hospital, etc.).
 * User may pick a suggestion (fills related fields via onSelect) or keep a custom name.
 */
const CatalogNameAutocomplete = ({
  value = "",
  onChange,
  onSelect,
  label,
  placeholder = "Search or type name",
  hint,
  searchFn,
  getOptionLabel,
  getOptionKey,
  getOptionSubtitle,
  error,
  minChars = CATALOG_SEARCH_MIN_CHARS,
  debounceMs = CATALOG_SEARCH_DEBOUNCE_MS,
  fetchOnEmpty = true,
  /** Row layout: match MultiRowFields height/border; hide hint text below input */
  compact = false,
}) => {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const {
    query,
    setQuery,
    results,
    loading,
    active,
    enable,
    close,
  } = useDebouncedCatalogSearch({
    searchFn,
    debounceMs,
    minChars,
    fetchOnEmpty,
  });

  useEffect(() => {
    setQuery(value || "");
  }, [value, setQuery]);

  useEffect(() => {
    const onDocClick = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [close]);

  const trimmedQuery = String(query || "").trim();
  const showMenu =
    active &&
    (loading || results.length > 0 || trimmedQuery.length >= minChars);

  const handleFocus = () => {
    enable();
  };

  const showSpinner = loading && active;
  const resolvedInputClass = compact
    ? `${rowInputClass} pr-10`
    : inputBaseClass;

  return (
    <div className={label || (!compact && hint) ? "space-y-1" : ""}>
      {label ? (
        <label className="block text-xs font-medium text-slate-700">{label}</label>
      ) : null}
      <div ref={wrapRef} className="relative group">
        <input
          ref={inputRef}
          type="text"
          className={`${resolvedInputClass} ${error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""}`}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={Boolean(error)}
          aria-expanded={showMenu}
          aria-haspopup="listbox"
          onFocus={handleFocus}
          onChange={(e) => {
            enable();
            const next = e.target.value;
            onChange?.(next);
            setQuery(next);
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-center w-10"
          aria-hidden="true"
        >
          {showSpinner ? (
            <span className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
          ) : (
            <SearchIcon
              size={16}
              className="text-slate-400 transition-colors group-focus-within:text-indigo-500"
            />
          )}
        </div>
        <SearchMenuPortal
          anchorRef={inputRef}
          menuRef={menuRef}
          open={showMenu}
        >
          {loading && results.length === 0 ? (
            <SearchMenuLoading />
          ) : results.length > 0 ? (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-100 sticky top-0">
                {trimmedQuery ? "Matches" : "Suggested"}
                {loading ? " · Updating…" : ""}
              </div>
              <ul role="listbox">
                {results.map((item) => (
                  <li key={getOptionKey(item)} role="option">
                    <button
                      type="button"
                      className="w-full text-left px-3.5 py-2.5 hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none border-b border-slate-50 last:border-0 transition-colors"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onSelect?.(item);
                        close();
                      }}
                    >
                      <div className="text-sm font-medium text-slate-900 leading-snug pr-2">
                        {getOptionLabel(item)}
                      </div>
                      {getOptionSubtitle?.(item) ? (
                        <div className="mt-0.5 text-[11px] text-slate-500 leading-snug">
                          {getOptionSubtitle(item)}
                        </div>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="px-3 py-3 text-xs text-slate-500 leading-relaxed">
              No library match. Keep typing to use a custom name.
            </div>
          )}
        </SearchMenuPortal>
      </div>
      {hint && !error && !compact ? (
        <p className="text-[10px] text-slate-500 leading-snug">{hint}</p>
      ) : null}
      {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
    </div>
  );
};

export default CatalogNameAutocomplete;
